export function attendanceBadgeClass(pct) {
  if (pct === null || pct === undefined) return 'bg-gray-100 text-gray-500';
  if (pct >= 75) return 'bg-emerald-100 text-emerald-700';
  if (pct >= 50) return 'bg-amber-100 text-amber-700';
  return 'bg-red-100 text-red-700';
}

export function attendanceBadgeStyle(pct) {
  if (pct === null || pct === undefined) return { background: '#f3f4f6', color: '#6b7280' };
  if (pct >= 75) return { background: '#d1fae5', color: '#047857' };
  if (pct >= 50) return { background: '#fef3c7', color: '#b45309' };
  return { background: '#fee2e2', color: '#b91c1c' };
}

// Groups a batch's subjects (already ordered by semester from the backend)
// into adjacent runs sharing the same semester, for a colSpan'd "Semester N"
// header row. All subjects in the same semester start/end on the same date,
// so the group's date range is just taken from whichever subject has it set.
export function groupSubjectsBySemester(subjects) {
  const groups = [];
  (subjects || []).forEach((subj) => {
    const last = groups[groups.length - 1];
    if (last && last.semester === subj.semester) {
      last.subjects.push(subj);
    } else {
      groups.push({ semester: subj.semester, subjects: [subj] });
    }
  });
  return groups.map((g) => ({
    semester: g.semester,
    subjects: g.subjects,
    startDate: g.subjects.find((s) => s.startDate)?.startDate || null,
    endDate: g.subjects.find((s) => s.endDate)?.endDate || null,
    allCompleted: g.subjects.every((s) => s.status === 'completed'),
    anyOngoing: g.subjects.some((s) => s.status === 'ongoing'),
  }));
}

// Semester header cell tier:
//   - end date passed and not every subject is completed -> overdue (red)
//   - end date not passed yet and at least one subject is ongoing -> in progress (blue)
//   - otherwise -> plain
function semesterGroupTier(group) {
  const endDatePassed = !!group.endDate && new Date(group.endDate) < new Date();
  if (endDatePassed && !group.allCompleted) return 'overdue';
  if (!endDatePassed && group.anyOngoing) return 'ongoing';
  return 'plain';
}

export function semesterGroupBadgeClass(group) {
  switch (semesterGroupTier(group)) {
    case 'overdue': return 'bg-red-100 text-red-800';
    case 'ongoing': return 'bg-blue-100 text-blue-800';
    default: return 'bg-gray-50 text-gray-500';
  }
}

export function semesterGroupBadgeStyle(group) {
  switch (semesterGroupTier(group)) {
    case 'overdue': return { background: '#fee2e2', color: '#991b1b' };
    case 'ongoing': return { background: '#dbeafe', color: '#1e40af' };
    default: return { background: '#f9fafb', color: '#6b7280' };
  }
}

export function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

// Whole days elapsed between a given date and today — used to show how long
// it's been since a student was last marked present.
export function daysSince(value) {
  if (!value) return null;
  const start = new Date(value);
  if (Number.isNaN(start.getTime())) return null;
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfDate = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  return Math.max(0, Math.round((startOfToday.getTime() - startOfDate.getTime()) / 86400000));
}

// Whole months elapsed between a given date and today — used to auto-show
// how long it's been since a student's University ID was last updated.
export function monthsSince(value) {
  if (!value) return null;
  const start = new Date(value);
  if (Number.isNaN(start.getTime())) return null;
  const now = new Date();
  let months = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
  if (now.getDate() < start.getDate()) months -= 1;
  return Math.max(0, months);
}

export function formatScheduleType(scheduleType) {
  if (!scheduleType) return '';
  return scheduleType.charAt(0).toUpperCase() + scheduleType.slice(1);
}

// HND courses grade on the D/M/P/R (Distinction/Merit/Pass/Refer) scale
// (assignment_submission.marks); every other course records a numeric score
// (topup_assignment_submission.marks) — this badge only applies to the
// former.
export function gradeBadgeClass(grade) {
  switch ((grade || '').toUpperCase()) {
    case 'D': return 'bg-purple-100 text-purple-700';
    case 'M': return 'bg-blue-100 text-blue-700';
    case 'P': return 'bg-emerald-100 text-emerald-700';
    case 'R': return 'bg-red-100 text-red-700';
    default: return 'bg-gray-100 text-gray-500';
  }
}

export function gradeBadgeStyle(grade) {
  switch ((grade || '').toUpperCase()) {
    case 'D': return { background: '#f3e8ff', color: '#7e22ce' };
    case 'M': return { background: '#dbeafe', color: '#1d4ed8' };
    case 'P': return { background: '#d1fae5', color: '#047857' };
    case 'R': return { background: '#fee2e2', color: '#b91c1c' };
    default: return { background: '#f3f4f6', color: '#6b7280' };
  }
}

// Difference in whole days between the actual and planned submission dates —
// positive means the actual date landed after the planned one (delayed),
// negative means it moved earlier. Returns null unless both dates are set.
export function submissionDateVarianceDays(subject) {
  if (!subject?.plannedSubmissionDate || !subject?.actualSubmissionDate) return null;
  const planned = new Date(subject.plannedSubmissionDate);
  const actual = new Date(subject.actualSubmissionDate);
  if (Number.isNaN(planned.getTime()) || Number.isNaN(actual.getTime())) return null;
  return Math.round((actual.getTime() - planned.getTime()) / (1000 * 60 * 60 * 24));
}

export function formatSubmissionDateVariance(subject) {
  const days = submissionDateVarianceDays(subject);
  if (days === null) return null;
  if (days === 0) return 'On time';
  return days > 0 ? `+${days}d` : `${days}d`;
}

// Subject column cell background:
//   - subject completed but no actual release date given -> yellow (flags a
//     subject marked done that never actually recorded a release date)
//   - subject completed and an actual release date exists -> light green
//   - subject ongoing, no actual submission date yet       -> blue
//   - actual submission date set, not yet passed            -> a bit stronger green
//   - actual submission date set and already passed         -> strong green
//   - none of the above                                     -> white (no tint)
function subjectCellTier(subject) {
  if (subject?.status === 'completed') {
    return subject.actualReleaseDate ? 1 : 4;
  }
  if (subject?.actualSubmissionDate) {
    const actualPastDue = new Date(subject.actualSubmissionDate) < new Date();
    return actualPastDue ? 3 : 2;
  }
  if (subject?.status === 'ongoing') return 5;
  return 0;
}

export function subjectCellBgClass(subject) {
  switch (subjectCellTier(subject)) {
    case 3: return 'bg-green-300';
    case 2: return 'bg-green-100';
    case 1: return 'bg-green-50';
    case 4: return 'bg-yellow-200';
    case 5: return 'bg-blue-100';
    default: return '';
  }
}

export function subjectCellBgStyle(subject) {
  switch (subjectCellTier(subject)) {
    case 3: return { backgroundColor: '#86efac' };
    case 2: return { backgroundColor: '#dcfce7' };
    case 1: return { backgroundColor: '#f0fdf4' };
    case 4: return { backgroundColor: '#fde047' };
    case 5: return { backgroundColor: '#dbeafe' };
    default: return undefined;
  }
}

// Whole days remaining until a batch's end date — negative once it's passed.
// Only meaningful while the batch is still ongoing.
export function daysRemaining(endDate) {
  if (!endDate) return null;
  const end = new Date(endDate);
  if (Number.isNaN(end.getTime())) return null;
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((end.getTime() - startOfToday.getTime()) / (1000 * 60 * 60 * 24));
}

export function formatDaysRemaining(endDate) {
  const days = daysRemaining(endDate);
  if (days === null) return null;
  if (days < 0) return `Overdue by ${Math.abs(days)}d`;
  if (days === 0) return 'Due today';
  return `${days}d remaining`;
}

export function daysRemainingBadgeClass(endDate) {
  const days = daysRemaining(endDate);
  if (days === null) return 'bg-gray-100 text-gray-500';
  if (days < 0) return 'bg-red-100 text-red-700';
  if (days <= 7) return 'bg-orange-100 text-orange-700';
  if (days <= 30) return 'bg-amber-100 text-amber-700';
  return 'bg-emerald-100 text-emerald-700';
}

export function daysRemainingBadgeStyle(endDate) {
  const days = daysRemaining(endDate);
  if (days === null) return { background: '#f3f4f6', color: '#6b7280' };
  if (days < 0) return { background: '#fee2e2', color: '#b91c1c' };
  if (days <= 7) return { background: '#ffedd5', color: '#c2410c' };
  if (days <= 30) return { background: '#fef3c7', color: '#b45309' };
  return { background: '#d1fae5', color: '#047857' };
}

export function formatMarkValue(mark, markType, submissionStatus) {
  if (mark === null || mark === undefined || mark === '') {
    return submissionStatus === 'not submitted' ? 'NS' : '—';
  }
  if (markType === 'numeric') {
    const num = Number(mark);
    return Number.isFinite(num) ? String(num) : '—';
  }
  return String(mark).toUpperCase();
}

// A failing result — numeric score below 43, or the 'R' (Refer) grade.
export function isFailingMark(mark, markType) {
  if (mark === null || mark === undefined || mark === '') return false;
  if (markType === 'numeric') {
    const num = Number(mark);
    return Number.isFinite(num) && num < 43;
  }
  return String(mark).toUpperCase() === 'R';
}
